// portal/app/src/routes/sessions/ManualSessionModal.tsx
import { useState, useEffect, useMemo, useCallback } from "react";
import { useQuery, useMutation } from "@apollo/client";
import {
  Dialog,
  DialogBackdrop,
  DialogPanel,
  DialogTitle,
  Label,
  Combobox,
  ComboboxInput,
  ComboboxButton,
  ComboboxOption,
  ComboboxOptions,
  Listbox,
  ListboxButton,
  ListboxOption,
  ListboxOptions,
  TabGroup,
  TabList,
  Tab,
  TabPanels,
  TabPanel,
} from "@headlessui/react";
import { ChevronDown, Check, X } from "lucide-react";
import {
  GET_PROVIDERS,
  GET_BOOKING_TYPES,
  GET_AVAILABILITIES_RANGE,
  CREATE_MANUAL_BOOKING,
  CREATE_SESSION,
} from "../../apollo/sessions";
import { GET_PERSON_OPTIONS } from "../../apollo/clients";
import { useToastPush } from "../../components/ui/Toast";
import { formatLocalDay, formatTimeRange } from "../../utils/sessionTime";

interface ManualSessionModalProps {
  open: boolean;
  onClose: () => void;
  onCreated?: () => void;
  /** Pre-select this client when the dialog opens (still editable). */
  presetCustomerId?: string;
}

const LABEL = "block text-[12.5px] font-medium text-ink-2 mb-1.5";
const ERR = "text-rust text-[11.5px] mt-1";

const METHODS = [
  { value: "video", label: "Video" },
  { value: "phone", label: "Phone" },
  { value: "in-person", label: "In person" },
] as const;

export function ManualSessionModal({ open, onClose, onCreated, presetCustomerId }: ManualSessionModalProps) {
  const toast = useToastPush();

  const [customerId, setCustomerId] = useState("");
  const [providerId, setProviderId] = useState("");
  const [bookingTypeId, setBookingTypeId] = useState("");
  const [method, setMethod] = useState<(typeof METHODS)[number]>(METHODS[0]);
  const [situation, setSituation] = useState("");
  const [tabIndex, setTabIndex] = useState(0);
  const [availabilityId, setAvailabilityId] = useState("");
  const [date, setDate] = useState("");
  const [startTime, setStartTime] = useState("");
  const [durationMinutes, setDurationMinutes] = useState("30");
  const [errors, setErrors] = useState<Record<string, string>>({});

  // Combobox query state
  const [clientQuery, setClientQuery] = useState("");
  const [providerQuery, setProviderQuery] = useState("");

  const timeMode: "slot" | "custom" = tabIndex === 0 ? "slot" : "custom";

  const { data: clientsData, refetch: refetchClients } = useQuery(GET_PERSON_OPTIONS, {
    variables: { page: 0, perPage: 500, sortField: "name", sortOrder: "ASC" },
    skip: !open,
  });
  const { data: providersData } = useQuery(GET_PROVIDERS, {
    variables: { perPage: 200, sortField: "firstName", sortOrder: "ASC" },
    skip: !open,
  });
  const { data: typesData } = useQuery(GET_BOOKING_TYPES, {
    variables: { perPage: 200, sortField: "name", sortOrder: "ASC" },
    skip: !open,
  });
  const { data: availData } = useQuery(GET_AVAILABILITIES_RANGE, {
    variables: { page: 0, perPage: 500, sortField: "date", sortOrder: "ASC" },
    skip: !open || timeMode !== "slot",
    fetchPolicy: "cache-and-network",
  });

  const clients: any[] = clientsData?.allPeople ?? [];
  const providers: any[] = providersData?.allProviders ?? [];
  const types: any[] = typesData?.allBookingTypes ?? [];

  // Filtered lists for combobox typeaheads
  const filteredClients = useMemo(() => {
    if (clientQuery === "") return clients;
    const q = clientQuery.toLowerCase();
    return clients.filter(
      (c: any) =>
        c.name?.toLowerCase().includes(q) || c.email?.toLowerCase().includes(q),
    );
  }, [clients, clientQuery]);

  const filteredProviders = useMemo(() => {
    if (providerQuery === "") return providers;
    const q = providerQuery.toLowerCase();
    return providers.filter(
      (l: any) =>
        `${l.firstName} ${l.lastName}`.toLowerCase().includes(q),
    );
  }, [providers, providerQuery]);

  // Open slots for the chosen provider + method, in the future, not yet booked.
  const slots = useMemo(() => {
    const all = availData?.allAvailabilities ?? [];
    const now = new Date();
    const supports = (a: any) =>
      method.value === "video" ? a.canVideo : method.value === "phone" ? a.canPhone : a.canInPerson;
    return all.filter(
      (a: any) =>
        a.providerId === providerId &&
        a.bookedAt == null &&
        supports(a) &&
        new Date(`${a.date}T${a.startTime}Z`) >= now,
    );
  }, [availData, providerId, method]);

  const [createBooking, { loading: creating }] = useMutation(CREATE_MANUAL_BOOKING);
  const [createSession] = useMutation(CREATE_SESSION);

  // Reset state on open.
  useEffect(() => {
    if (open) {
      setCustomerId(presetCustomerId ?? "");
      setProviderId("");
      setBookingTypeId("");
      setMethod(METHODS[0]);
      setSituation("");
      setTabIndex(0);
      setAvailabilityId("");
      setDate("");
      setStartTime("");
      setDurationMinutes("30");
      setErrors({});
      setClientQuery("");
      setProviderQuery("");
    }
  }, [open, presetCustomerId]);

  // Refetch clients when the operator returns from the "add client" tab.
  useEffect(() => {
    if (!open) return;
    const onFocus = () => refetchClients();
    window.addEventListener("focus", onFocus);
    return () => window.removeEventListener("focus", onFocus);
  }, [open, refetchClients]);

  const validate = useCallback((): boolean => {
    const e: Record<string, string> = {};
    if (!customerId) e.customerId = "Choose a client.";
    if (!providerId) e.providerId = "Choose a provider.";
    if (!bookingTypeId) e.bookingTypeId = "Choose a session type.";
    if (timeMode === "slot") {
      if (!availabilityId) e.availabilityId = "Choose an open slot.";
    } else {
      if (!date) e.date = "Pick a date.";
      if (!startTime) e.startTime = "Pick a start time.";
      if (!durationMinutes || Number(durationMinutes) <= 0) e.durationMinutes = "Enter minutes.";
    }
    setErrors(e);
    return Object.keys(e).length === 0;
  }, [customerId, providerId, bookingTypeId, timeMode, availabilityId, date, startTime, durationMinutes]);

  const handleSubmit = async (ev: React.FormEvent) => {
    ev.preventDefault();
    if (!validate()) return;
    try {
      const { data } = await createBooking({
        variables: {
          customerIds: [customerId],
          providerId,
          bookingTypeId,
          method: method.value,
          situation: situation.trim() || null,
          availabilityId: timeMode === "slot" ? availabilityId : null,
          date: timeMode === "custom" ? date : null,
          startTime: timeMode === "custom" ? startTime : null,
          durationMinutes: timeMode === "custom" ? Number(durationMinutes) : null,
        },
      });
      const newId = data?.createManualBooking?.id;
      // Provision the video room (skip for in-person, which has no room).
      if (newId && method.value !== "in-person") {
        try {
          await createSession({ variables: { bookingId: newId } });
        } catch {
          toast({
            kind: "info",
            title: "Session created",
            description: "The video room will be set up when you start the session.",
          });
          onClose();
          onCreated?.();
          return;
        }
      }
      toast({ kind: "success", title: "Session created" });
      onClose();
      onCreated?.();
    } catch (err) {
      toast({ kind: "error", title: "Could not create session", description: (err as Error).message });
    }
  };

  // -- Helpers for display values ------------------------------------------------

  const selectedClient = clients.find((c: any) => c.id === customerId) ?? null;
  const selectedProvider = providers.find((l: any) => l.id === providerId) ?? null;
  const selectedType = types.find((t: any) => t.id === bookingTypeId) ?? null;
  const selectedSlot = slots.find((a: any) => a.id === availabilityId) ?? null;

  function clientDisplayValue(c: any): string {
    if (!c) return "";
    return c.name ?? "";
  }

  function providerDisplayValue(l: any): string {
    if (!l) return "";
    return `${l.firstName} ${l.lastName}`;
  }

  function slotLabel(a: any): string {
    return `${formatLocalDay(a.date, a.startTime)} · ${formatTimeRange(a.date, a.startTime, a.endTime)}`;
  }

  // -- Shared Tailwind classes ---------------------------------------------------

  const inputBase =
    "w-full px-3 py-2 text-[13px] rounded-md bg-canvas hair border focus:outline-none focus:border-tide text-ink placeholder:text-ink-5";
  const comboboxOptionsClass =
    "absolute z-10 mt-1 max-h-52 w-full overflow-auto rounded-md bg-paper-hi py-1 shadow-lift ring-1 ring-ink/5 focus:outline-none empty:hidden";

  return (
    <Dialog open={open} onClose={onClose} className="relative z-50">
      <DialogBackdrop className="fixed inset-0 bg-ink/40 backdrop-blur-sm" />

      <div className="fixed inset-0 flex items-center justify-center p-4">
        <DialogPanel className="bg-paper-hi rounded-lg shadow-lift w-full max-w-lg overflow-hidden">
          <form onSubmit={handleSubmit}>
            {/* Header */}
            <div className="flex items-center justify-between px-5 py-4 hair border-b">
              <DialogTitle as="h2" className="text-[15px] font-medium text-ink">New session</DialogTitle>
              <button
                type="button"
                onClick={onClose}
                className="w-7 h-7 rounded hover:bg-paper-2 flex items-center justify-center text-ink-4"
                aria-label="Close"
              >
                <X size={14} strokeWidth={2} />
              </button>
            </div>

            {/* Body */}
            <div className="px-5 py-4 space-y-4 max-h-[70vh] overflow-y-auto">
              {/* Client combobox */}
              <div>
                <Combobox
                  value={selectedClient}
                  by="id"
                  onChange={(c: any) => {
                    setCustomerId(c?.id ?? "");
                  }}
                  onClose={() => setClientQuery("")}
                >
                  <Label className={LABEL}>Client <span className="text-rust">*</span></Label>
                  <div className="relative">
                    <ComboboxInput
                      className={`${inputBase} pr-8 ${errors.customerId ? "!border-rust" : ""}`}
                      displayValue={clientDisplayValue}
                      onChange={(e) => setClientQuery(e.target.value)}
                      placeholder="Search clients..."
                      autoFocus
                    />
                    <ComboboxButton className="absolute inset-y-0 right-0 flex items-center pr-2">
                      <ChevronDown size={14} className="text-ink-4" />
                    </ComboboxButton>
                    <ComboboxOptions className={comboboxOptionsClass}>
                      {filteredClients.length === 0 ? (
                        <div className="py-2 px-4 text-[13px] text-ink-4">No clients found</div>
                      ) : filteredClients.map((c: any) => (
                        <ComboboxOption
                          key={c.id}
                          value={c}
                          className="group relative cursor-pointer select-none py-2 pl-9 pr-4 text-[13px] text-ink data-[focus]:bg-tide/10"
                        >
                          {({ selected }) => (
                            <>
                              <div>
                                <span className="block truncate">{c.name}</span>
                                {c.email && (
                                  <span className="block truncate text-[11.5px] text-ink-4">{c.email}</span>
                                )}
                              </div>
                              {selected && (
                                <span className="absolute inset-y-0 left-0 flex items-center pl-2 text-tide">
                                  <Check size={14} />
                                </span>
                              )}
                            </>
                          )}
                        </ComboboxOption>
                      ))}
                    </ComboboxOptions>
                  </div>
                </Combobox>
                {errors.customerId && <p className={ERR}>{errors.customerId}</p>}
                <a
                  href="/clients?new=1"
                  target="_blank"
                  rel="noopener noreferrer"
                  className="inline-block mt-1.5 text-[11.5px] text-tide hover:underline"
                >
                  + Add a new client (opens in a new tab)
                </a>
              </div>

              {/* Provider combobox */}
              <div>
                <Combobox
                  value={selectedProvider}
                  by="id"
                  onChange={(l: any) => {
                    setProviderId(l?.id ?? "");
                    setAvailabilityId("");
                  }}
                  onClose={() => setProviderQuery("")}
                >
                  <Label className={LABEL}>Provider <span className="text-rust">*</span></Label>
                  <div className="relative">
                    <ComboboxInput
                      className={`${inputBase} pr-8 ${errors.providerId ? "!border-rust" : ""}`}
                      displayValue={providerDisplayValue}
                      onChange={(e) => setProviderQuery(e.target.value)}
                      placeholder="Search providers..."
                    />
                    <ComboboxButton className="absolute inset-y-0 right-0 flex items-center pr-2">
                      <ChevronDown size={14} className="text-ink-4" />
                    </ComboboxButton>
                    <ComboboxOptions className={comboboxOptionsClass}>
                      {filteredProviders.length === 0 ? (
                        <div className="py-2 px-4 text-[13px] text-ink-4">No providers found</div>
                      ) : filteredProviders.map((l: any) => (
                        <ComboboxOption
                          key={l.id}
                          value={l}
                          className="group relative cursor-pointer select-none py-2 pl-9 pr-4 text-[13px] text-ink data-[focus]:bg-tide/10"
                        >
                          {({ selected }) => (
                            <>
                              <span className="block truncate">{l.firstName} {l.lastName}</span>
                              {selected && (
                                <span className="absolute inset-y-0 left-0 flex items-center pl-2 text-tide">
                                  <Check size={14} />
                                </span>
                              )}
                            </>
                          )}
                        </ComboboxOption>
                      ))}
                    </ComboboxOptions>
                  </div>
                </Combobox>
                {errors.providerId && <p className={ERR}>{errors.providerId}</p>}
              </div>

              {/* Session type + Method */}
              <div className="grid grid-cols-2 gap-3">
                {/* Session type listbox */}
                <div>
                  <Listbox
                    value={selectedType}
                    by="id"
                    onChange={(t: any) => setBookingTypeId(t?.id ?? "")}
                  >
                    <Label className={LABEL}>Session type <span className="text-rust">*</span></Label>
                    <div className="relative">
                      <ListboxButton
                        className={`${inputBase} pr-8 text-left ${!selectedType ? "text-ink-5" : ""} ${errors.bookingTypeId ? "!border-rust" : ""}`}
                      >
                        <span className="block truncate">
                          {selectedType ? selectedType.name : "Select..."}
                        </span>
                        <span className="pointer-events-none absolute inset-y-0 right-0 flex items-center pr-2">
                          <ChevronDown size={14} className="text-ink-4" />
                        </span>
                      </ListboxButton>
                      <ListboxOptions className={comboboxOptionsClass}>
                        {types.map((t: any) => (
                          <ListboxOption
                            key={t.id}
                            value={t}
                            className="group relative cursor-pointer select-none py-2 pl-9 pr-4 text-[13px] text-ink data-[focus]:bg-tide/10"
                          >
                            {({ selected }) => (
                              <>
                                <span className="block truncate">{t.name}</span>
                                {selected && (
                                  <span className="absolute inset-y-0 left-0 flex items-center pl-2 text-tide">
                                    <Check size={14} />
                                  </span>
                                )}
                              </>
                            )}
                          </ListboxOption>
                        ))}
                      </ListboxOptions>
                    </div>
                  </Listbox>
                  {errors.bookingTypeId && <p className={ERR}>{errors.bookingTypeId}</p>}
                </div>

                {/* Method listbox */}
                <div>
                  <Listbox
                    value={method}
                    onChange={(m: (typeof METHODS)[number]) => {
                      setMethod(m);
                      setAvailabilityId("");
                    }}
                  >
                    <Label className={LABEL}>Method <span className="text-rust">*</span></Label>
                    <div className="relative">
                      <ListboxButton className={`${inputBase} pr-8 text-left`}>
                        <span className="block truncate">{method.label}</span>
                        <span className="pointer-events-none absolute inset-y-0 right-0 flex items-center pr-2">
                          <ChevronDown size={14} className="text-ink-4" />
                        </span>
                      </ListboxButton>
                      <ListboxOptions className={comboboxOptionsClass}>
                        {METHODS.map((m) => (
                          <ListboxOption
                            key={m.value}
                            value={m}
                            className="group relative cursor-pointer select-none py-2 pl-9 pr-4 text-[13px] text-ink data-[focus]:bg-tide/10"
                          >
                            {({ selected }) => (
                              <>
                                <span className="block truncate">{m.label}</span>
                                {selected && (
                                  <span className="absolute inset-y-0 left-0 flex items-center pl-2 text-tide">
                                    <Check size={14} />
                                  </span>
                                )}
                              </>
                            )}
                          </ListboxOption>
                        ))}
                      </ListboxOptions>
                    </div>
                  </Listbox>
                </div>
              </div>

              {/* Time: Open slot / Custom time tabs */}
              <div>
                <TabGroup selectedIndex={tabIndex} onChange={setTabIndex}>
                  <TabList className="flex gap-2 mb-2">
                    <Tab className="px-3 py-1.5 rounded-md text-[12.5px] focus:outline-none data-[selected]:bg-tide data-[selected]:text-paper-hi hair border data-[selected]:border-transparent text-ink-2">
                      Open slot
                    </Tab>
                    <Tab className="px-3 py-1.5 rounded-md text-[12.5px] focus:outline-none data-[selected]:bg-tide data-[selected]:text-paper-hi hair border data-[selected]:border-transparent text-ink-2">
                      Custom time
                    </Tab>
                  </TabList>

                  <TabPanels>
                    {/* Open slot panel */}
                    <TabPanel>
                      <Listbox
                        value={selectedSlot}
                        by="id"
                        onChange={(a: any) => setAvailabilityId(a?.id ?? "")}
                        disabled={!providerId}
                      >
                        <div className="relative">
                          <ListboxButton
                            className={`${inputBase} pr-8 text-left ${!selectedSlot ? "text-ink-5" : ""} ${errors.availabilityId ? "!border-rust" : ""} disabled:opacity-60 disabled:cursor-not-allowed`}
                          >
                            <span className="block truncate">
                              {!providerId
                                ? "Choose a provider first"
                                : selectedSlot
                                  ? slotLabel(selectedSlot)
                                  : slots.length
                                    ? "Select an open slot..."
                                    : "No open slots for this provider/method"}
                            </span>
                            <span className="pointer-events-none absolute inset-y-0 right-0 flex items-center pr-2">
                              <ChevronDown size={14} className="text-ink-4" />
                            </span>
                          </ListboxButton>
                          <ListboxOptions className={comboboxOptionsClass}>
                            {slots.map((a: any) => (
                              <ListboxOption
                                key={a.id}
                                value={a}
                                className="group relative cursor-pointer select-none py-2 pl-9 pr-4 text-[13px] text-ink data-[focus]:bg-tide/10"
                              >
                                {({ selected }) => (
                                  <>
                                    <span className="block truncate">{slotLabel(a)}</span>
                                    {selected && (
                                      <span className="absolute inset-y-0 left-0 flex items-center pl-2 text-tide">
                                        <Check size={14} />
                                      </span>
                                    )}
                                  </>
                                )}
                              </ListboxOption>
                            ))}
                          </ListboxOptions>
                        </div>
                      </Listbox>
                      {errors.availabilityId && <p className={ERR}>{errors.availabilityId}</p>}
                    </TabPanel>

                    {/* Custom time panel */}
                    <TabPanel>
                      <div className="grid grid-cols-3 gap-3">
                        <div>
                          <label htmlFor="ms-date" className={LABEL}>Date</label>
                          <input
                            id="ms-date"
                            type="date"
                            value={date}
                            onChange={(e) => setDate(e.target.value)}
                            className={`${inputBase} ${errors.date ? "!border-rust" : ""}`}
                          />
                        </div>
                        <div>
                          <label htmlFor="ms-start" className={LABEL}>Start</label>
                          <input
                            id="ms-start"
                            type="time"
                            value={startTime}
                            onChange={(e) => setStartTime(e.target.value)}
                            className={`${inputBase} ${errors.startTime ? "!border-rust" : ""}`}
                          />
                        </div>
                        <div>
                          <label htmlFor="ms-dur" className={LABEL}>Minutes</label>
                          <input
                            id="ms-dur"
                            type="number"
                            min={1}
                            value={durationMinutes}
                            onChange={(e) => setDurationMinutes(e.target.value)}
                            className={`${inputBase} ${errors.durationMinutes ? "!border-rust" : ""}`}
                          />
                        </div>
                      </div>
                    </TabPanel>
                  </TabPanels>
                </TabGroup>
              </div>

              {/* Situation / notes */}
              <div>
                <label htmlFor="ms-sit" className={LABEL}>Notes <span className="text-ink-4">(optional)</span></label>
                <textarea
                  id="ms-sit"
                  value={situation}
                  onChange={(e) => setSituation(e.target.value)}
                  rows={2}
                  placeholder="What's this session about?"
                  className={inputBase}
                />
              </div>
            </div>

            {/* Footer */}
            <div className="flex justify-end gap-2 px-5 py-4 hair border-t">
              <button
                type="button"
                onClick={onClose}
                className="px-4 py-2 rounded-md hair border text-[13px] text-ink-2 hover:bg-paper-2"
              >
                Cancel
              </button>
              <button
                type="submit"
                disabled={creating}
                className="px-4 py-2 rounded-md bg-tide text-paper-hi text-[13px] font-medium hover:bg-tide-hi disabled:opacity-60 disabled:cursor-not-allowed"
              >
                {creating ? "Creating…" : "Create session"}
              </button>
            </div>
          </form>
        </DialogPanel>
      </div>
    </Dialog>
  );
}
